import type { FilterGroup, FilterNode, FilterCondition, ColumnType, FilterOperator } from '../types';
import { getOperatorsForType } from '../config/filter-operators';
import { MAX_NESTING_DEPTH } from '../config/constants';
import { getColumnDefs } from './app-state';

let nodeIdCounter = 0;

let filterRoot: FilterGroup = {
  type: 'group',
  id: 'root',
  matchMode: 'ALL',
  children: []
};

export function getFilterRoot(): FilterGroup {
  return filterRoot;
}

export function setFilterRoot(root: FilterGroup): void {
  filterRoot = root;
}

export function resetFilterState(): void {
  nodeIdCounter = 0;
  filterRoot = {
    type: 'group',
    id: 'root',
    matchMode: 'ALL',
    children: []
  };
}

export function getNextNodeId(): number {
  return ++nodeIdCounter;
}

export function getColumnType(columnName: string): ColumnType {
  const columnDefs = getColumnDefs();
  const col = columnDefs.find(c => c.field === columnName);
  if (col && col.type === 'numericColumn') {
    return 'number';
  }
  return 'text';
}

export function findNodeById(id: number | 'root', node: FilterNode = filterRoot): FilterNode | null {
  if (node.id === id) return node;
  if (node.type === 'group' && node.children) {
    for (const child of node.children) {
      const found = findNodeById(id, child);
      if (found) return found;
    }
  }
  return null;
}

export function findParentNode(
  targetId: number | 'root',
  node: FilterNode = filterRoot,
  parent: FilterGroup | null = null
): FilterGroup | null {
  if (node.id === targetId) return parent;
  if (node.type === 'group' && node.children) {
    for (const child of node.children) {
      const found = findParentNode(targetId, child, node);
      if (found) return found;
    }
  }
  return null;
}

export function getNodeDepth(
  nodeId: number | 'root',
  node: FilterNode = filterRoot,
  depth: number = 1
): number | null {
  if (node.id === nodeId) return depth;
  if (node.type === 'group' && node.children) {
    for (const child of node.children) {
      const found = getNodeDepth(nodeId, child, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

export function canAddGroup(parentId: number | 'root'): boolean {
  const depth = getNodeDepth(parentId);
  return depth !== null && depth < MAX_NESTING_DEPTH;
}

export function addCondition(parentId: number | 'root' = 'root'): FilterCondition | null {
  const columnDefs = getColumnDefs();
  if (columnDefs.length === 0) return null;

  const parent = findNodeById(parentId);
  if (!parent || parent.type !== 'group') return null;

  const columnName = columnDefs[0].field as string;
  const columnType = getColumnType(columnName);
  const operators = getOperatorsForType(columnType);

  const condition: FilterCondition = {
    type: 'condition',
    id: getNextNodeId(),
    column: columnName,
    operator: operators[0].value,
    value: '',
    value2: ''
  };

  parent.children.push(condition);
  return condition;
}

export function addGroup(parentId: number | 'root' = 'root'): FilterGroup | null {
  const parent = findNodeById(parentId);
  if (!parent || parent.type !== 'group') return null;

  if (!canAddGroup(parentId)) {
    console.warn('Max nesting depth reached');
    return null;
  }

  const group: FilterGroup = {
    type: 'group',
    id: getNextNodeId(),
    matchMode: 'ANY',
    children: []
  };

  parent.children.push(group);
  return group;
}

export function removeNode(id: number): boolean {
  const parent = findParentNode(id);
  if (!parent) return false;

  const index = parent.children.findIndex(child => child.id === id);
  if (index !== -1) {
    parent.children.splice(index, 1);
    return true;
  }
  return false;
}

export function updateNode(
  id: number | 'root',
  field: 'column' | 'operator' | 'value' | 'value2' | 'matchMode',
  value: string
): boolean {
  const node = findNodeById(id);
  if (!node) return false;

  let needsRerender = false;

  if (node.type === 'condition') {
    if (field === 'column') {
      node.column = value;
      const columnType = getColumnType(value);
      const operators = getOperatorsForType(columnType);
      node.operator = operators[0].value;
      node.value = '';
      node.value2 = '';
      needsRerender = true;
    } else if (field === 'operator') {
      node.operator = value as FilterOperator;
      node.value = '';
      node.value2 = '';
      needsRerender = true;
    } else if (field === 'value') {
      node.value = value;
    } else if (field === 'value2') {
      node.value2 = value;
    }
  } else if (node.type === 'group') {
    if (field === 'matchMode') {
      node.matchMode = value as 'ALL' | 'ANY';
      needsRerender = true;
    }
  }

  return needsRerender;
}

export function toggleMatchMode(id: number | 'root'): boolean {
  const node = findNodeById(id);
  if (!node || node.type !== 'group') return false;
  node.matchMode = node.matchMode === 'ALL' ? 'ANY' : 'ALL';
  return true;
}
